import {
  type ITrustAccount,
  TRUST_ACCOUNT_OWNER_TYPES,
  TRUST_ACCOUNT_PURPOSES,
} from 'components/TrustLedgerGrid/types'
import { useBankNameLookup } from 'hooks/useBankNameLookup'
import { useEffect, useState } from 'react'
import { createTrustAccount, filterParticipants, getAccountingVendors, updateTrustAccount } from 'services'
import { Modal } from 'stories/components'
import { validateBankAccountNumber, validateRoutingNumber } from 'utils/bank'

/**
 * Add or edit a trust account.
 *
 * An account carries the purposes it may be used for, and optionally the lender or vendor whose
 * account it is. A company account can be marked the default for its purposes: that is what a loan
 * falls back to when neither the loan nor its lender names one. Only one company account may be
 * the default for a given purpose, and the backend refuses a second.
 */
export default function TrustAccountModal({
  account,
  onClose,
}: {
  account: ITrustAccount | null
  onClose: (changed: boolean) => void
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [owners, setOwners] = useState<Array<{ id: number; name: string }>>([])
  const { resolveBankName } = useBankNameLookup()
  const [values, setValues] = useState({
    name: account?.name || '',
    bankName: account?.bankName || '',
    accountHolder: account?.accountHolder || '',
    routingNo: account?.routingNo || '',
    accountNo: account?.accountNo || '',
    glAccountNo: account?.glAccountNo || '',
    purposes: account?.purposes || ([] as string[]),
    ownerType: account?.ownerType || 'Company',
    ownerId: account?.ownerId || 0,
    isDefault: !!account?.isDefault,
    isActive: account ? account.isActive : true,
    openingBalance: account?.openingBalance ? String(account.openingBalance) : '',
    openingBalanceDate: account?.openingBalanceDate || '',
    note: account?.note || '',
  })

  const set = (key: string, value: any) => setValues((prev) => ({ ...prev, [key]: value }))

  const routingError = validateRoutingNumber(values.routingNo)
  const accountError = validateBankAccountNumber(values.accountNo)

  const onChangeRoutingNo = (value: string) => {
    set('routingNo', value)
    resolveBankName(value, values.bankName).then((bankName) => bankName && set('bankName', bankName))
  }

  // Owner choices follow the owner type: lenders come from the participant directory, vendors
  // from the accounting vendor list.
  useEffect(() => {
    if (values.ownerType === 'Lender') {
      filterParticipants({ query: '', count: 500, skip: 0 })
        .then((res: any) =>
          setOwners((res?.data || []).map((item: any) => ({ id: item.id, name: item.payeeName || item.fullName }))),
        )
        .catch(() => setOwners([]))
    } else if (values.ownerType === 'Vendor') {
      getAccountingVendors({ query: '', count: 500, pageNum: 0, activeOnly: true })
        .then((res: any) => setOwners((res?.results || []).map((item: any) => ({ id: item.id, name: item.name }))))
        .catch(() => setOwners([]))
    } else {
      setOwners([])
    }
  }, [values.ownerType])

  const togglePurpose = (purpose: string) => {
    const next = values.purposes.includes(purpose)
      ? values.purposes.filter((item) => item !== purpose)
      : [...values.purposes, purpose]
    set('purposes', next)
  }

  const onSubmit = async () => {
    setError('')
    if (routingError || accountError) {
      setError(routingError || accountError)
      return
    }
    const payload = {
      ...values,
      ownerId: values.ownerType === 'Company' ? 0 : Number(values.ownerId) || 0,
      isDefault: values.ownerType === 'Company' ? values.isDefault : false,
      openingBalance: Number(String(values.openingBalance).replace(/,/g, '')) || 0,
    }
    setLoading(true)
    try {
      if (account) await updateTrustAccount(account.id, payload)
      else await createTrustAccount(payload)
      onClose(true)
    } catch (err: any) {
      setError(err?.message || 'Could not save the trust account')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={account ? `Update Trust Account - ${account.name}` : 'Add Trust Account'}
      titleOkay="Save"
      loading={loading}
      onClose={() => onClose(false)}
      onOk={onSubmit}
      isOpen
    >
      <div className="text-gray-900 w-144 grid grid-cols-2 gap-4 mb-3 mt-2">
        <div className="col-span-2">
          <div className="text-[13px] text-gray-500 mb-1">Account Name</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </div>

        <div className="col-span-2">
          <div className="text-[13px] text-gray-500 mb-1">Used For</div>
          <div className="flex gap-4 flex-wrap">
            {Object.keys(TRUST_ACCOUNT_PURPOSES).map((purpose) => (
              <label key={purpose} className="text-[14px] flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={values.purposes.includes(purpose)}
                  onChange={() => togglePurpose(purpose)}
                />
                {TRUST_ACCOUNT_PURPOSES[purpose]}
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Whose Account</div>
          <select
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.ownerType}
            onChange={(e) => {
              set('ownerType', e.target.value)
              set('ownerId', 0)
            }}
          >
            {Object.keys(TRUST_ACCOUNT_OWNER_TYPES).map((type) => (
              <option key={type} value={type}>
                {TRUST_ACCOUNT_OWNER_TYPES[type]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">
            {values.ownerType === 'Company' ? 'Owner' : `${values.ownerType} Record`}
          </div>
          <select
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.ownerId}
            disabled={values.ownerType === 'Company'}
            onChange={(e) => set('ownerId', e.target.value)}
          >
            <option value={0}>{values.ownerType === 'Company' ? 'The company' : '- Select -'}</option>
            {owners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Bank</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.bankName}
            onChange={(e) => set('bankName', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Account Holder</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.accountHolder}
            onChange={(e) => set('accountHolder', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Routing Number</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.routingNo}
            onChange={(e) => onChangeRoutingNo(e.target.value)}
          />
          {!!routingError && <div className="text-[12px] text-rose-600 mt-1">{routingError}</div>}
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Account Number</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.accountNo}
            onChange={(e) => set('accountNo', e.target.value)}
          />
          {!!accountError && <div className="text-[12px] text-rose-600 mt-1">{accountError}</div>}
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">GL Account</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.glAccountNo}
            onChange={(e) => set('glAccountNo', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Opening Balance</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.openingBalance}
            onChange={(e) => set('openingBalance', e.target.value)}
          />
        </div>

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Opening Balance Date</div>
          <input
            type="date"
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.openingBalanceDate}
            onChange={(e) => set('openingBalanceDate', e.target.value)}
          />
        </div>

        <div className="flex items-end gap-6">
          {values.ownerType === 'Company' && (
            <label className="text-[14px] flex items-center gap-2 pb-2">
              <input type="checkbox" checked={values.isDefault} onChange={() => set('isDefault', !values.isDefault)} />
              Default for its purposes
            </label>
          )}
          <label className="text-[14px] flex items-center gap-2 pb-2">
            <input type="checkbox" checked={values.isActive} onChange={() => set('isActive', !values.isActive)} />
            Active
          </label>
        </div>

        <div className="col-span-2">
          <div className="text-[13px] text-gray-500 mb-1">Note</div>
          <input
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={values.note}
            onChange={(e) => set('note', e.target.value)}
          />
        </div>

        {!!error && <div className="col-span-2 text-[13px] text-red-600">{error}</div>}
      </div>
    </Modal>
  )
}
